import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileScreenButton,
  faClock,
  faSpinner,
  faBookOpen,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import SideMenuComponentsHeader from "../GenericComponents/Headers/SideMenuComponentsHeader";
import SideDetails from "../GenericComponents/SideDetails";
import Footer from "../GenericComponents/Footer";
import SegmentedControl from "../GenericComponents/SegmentedControl";
import InfoDetailsList from "../GenericComponents/InfoDetailsList";
import SignInCodePanel from "../GenericComponents/SignInCodePanel";
import OutlineActionButton from "../GenericComponents/OutlineActionButton";
import CustomLoader from "../GenericComponents/CustomLoader";
import CustomModal from "../GenericComponents/CustomModal";
import WatchSignInCodeCard from "./WatchSignInCodeCard";
import {
  DEVICE_TYPES,
  buildDeviceDetailItems,
  mapLatestDeviceToView,
  SIGN_IN_CODE_EXPIRY_SECONDS,
  INITIAL_LAST_SYNCED,
} from "./mockData";
import {
  fetchWatchSignInCodeStatus,
  fetchWatchSignInCode,
  fetchLatestDevice,
  pingDevice,
  getAcumaAppType,
} from "./watchSignInApi";
import styles from "./ConnectedDevice.module.css";

const DEVICE_OPTIONS = [
  {
    value: DEVICE_TYPES.MOBILE,
    label: "Mobile",
    icon: <FontAwesomeIcon icon={faMobileScreenButton} />,
  },
  {
    value: DEVICE_TYPES.WATCH,
    label: "Watch",
    icon: <FontAwesomeIcon icon={faClock} />,
  },
];

const SETUP_GUIDES = {
  android: {
    label: "Android",
    src: encodeURI("/document/Acuma Guide Current 2026 Android.pdf"),
  },
  ios: {
    label: "iOS",
    src: encodeURI("/document/Acuma Guide Current 2026 iOS.pdf"),
  },
};

const SETUP_GUIDE_OPTIONS = Object.entries(SETUP_GUIDES).map(
  ([value, guide]) => ({ value, label: guide.label }),
);

const ConnectedDevice = () => {
  const profileData = useSelector((store) => store.dashboard.profileData);
  const user = useSelector((store) => store.user);
  const patientSubscription = useSelector(
    (store) => store.appstate.patientSubscription,
  );
  const patientId = profileData?.id;
  const hasActiveSubscription =
    !user?.isSubscriptionApplicable ||
    patientSubscription?.status === 1 ||
    patientSubscription?.status === 5;

  const [selectedType, setSelectedType] = useState(DEVICE_TYPES.MOBILE);
  const [loadedType, setLoadedType] = useState(null);
  const [device, setDevice] = useState(() =>
    mapLatestDeviceToView({}, DEVICE_TYPES.MOBILE),
  );
  const [signInCode, setSignInCode] = useState(null);
  const [codeExpiresInSeconds, setCodeExpiresInSeconds] = useState(
    SIGN_IN_CODE_EXPIRY_SECONDS,
  );
  const [copied, setCopied] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(INITIAL_LAST_SYNCED);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [setupGuidePlatform, setSetupGuidePlatform] = useState("android");

  const detailItems = useMemo(() => buildDeviceDetailItems(device), [device]);
  const isWatch = selectedType === DEVICE_TYPES.WATCH;

  const mapHasAnyDeviceData = useCallback((mappedDevice) => {
    return Boolean(
      mappedDevice?.manufacturer ||
      mappedDevice?.modelName ||
      mappedDevice?.os ||
      mappedDevice?.appVersion ||
      mappedDevice?.osApiLevel ||
      mappedDevice?.oemUi ||
      mappedDevice?.oemUiVersion ||
      mappedDevice?.lastLogin ||
      mappedDevice?.lastLogout ||
      mappedDevice?.lastActivity ||
      mappedDevice?.lastSynced,
    );
  }, []);

  const hasDeviceData = useMemo(
    () => mapHasAnyDeviceData(device),
    [device, mapHasAnyDeviceData],
  );

  const emptyDeviceMessage = isWatch
    ? "No device information available for the watch"
    : "No device information available for the Mobile";

  const applySignInCode = useCallback((payload) => {
    if (!payload?.code) {
      setSignInCode(null);
      return;
    }
    setSignInCode(String(payload.code));
    setCodeExpiresInSeconds(
      payload.expiresInSeconds || SIGN_IN_CODE_EXPIRY_SECONDS,
    );
    setCopied(false);
  }, []);

  const loadLatestDevice = useCallback(async (deviceType) => {
    const acumaAppType = getAcumaAppType(deviceType);
    setDeviceLoading(true);
    try {
      const payload = await fetchLatestDevice(acumaAppType);
      const mapped = mapLatestDeviceToView(payload, deviceType);
      setDevice(mapped);
      setLastSynced(mapped.lastSynced ?? INITIAL_LAST_SYNCED);
      setLoadedType(deviceType);
    } catch (err) {
      console.log("Unable to fetch latest device", err);
      setDevice(mapLatestDeviceToView({}, deviceType));
      setLastSynced(INITIAL_LAST_SYNCED);
      setLoadedType(deviceType);
    } finally {
      setDeviceLoading(false);
    }
  }, []);

  const loadValidWatchSignInCode = useCallback(async () => {
    if (!patientId) return;

    setCodeLoading(true);
    setSignInCode(null);
    setCopied(false);

    try {
      const status = await fetchWatchSignInCodeStatus(patientId);
      const normalizedStatus =
        typeof status === "string" ? status.toLowerCase() : status;

      if (normalizedStatus === "valid") {
        const codePayload = await fetchWatchSignInCode(patientId, false);
        applySignInCode(codePayload);
      }
    } catch (err) {
      console.log("Unable to fetch watch sign-in code status/code", err);
      setSignInCode(null);
    } finally {
      setCodeLoading(false);
    }
  }, [patientId, applySignInCode]);

  useEffect(() => {
    if (!hasActiveSubscription) {
      setLoadedType(null);
      setDevice(mapLatestDeviceToView({}, DEVICE_TYPES.MOBILE));
      setLastSynced(INITIAL_LAST_SYNCED);
      return;
    }
    if (loadedType === selectedType) return;
    loadLatestDevice(selectedType);
  }, [hasActiveSubscription, loadedType, selectedType, loadLatestDevice]);

  useEffect(() => {
    if (!hasActiveSubscription) return;
    if (selectedType !== DEVICE_TYPES.WATCH) {
      setSignInCode(null);
      setCopied(false);
      return;
    }

    loadValidWatchSignInCode();
  }, [
    hasActiveSubscription,
    selectedType,
    patientId,
    loadValidWatchSignInCode,
  ]);

  const handleTypeChange = useCallback((type) => {
    setSelectedType(type);
    setSignInCode(null);
    setCopied(false);
    setCodeExpiresInSeconds(SIGN_IN_CODE_EXPIRY_SECONDS);
  }, []);

  const handleSyncDeviceInfo = useCallback(async () => {
    const acumaAppType = getAcumaAppType(selectedType);
    setSyncing(true);
    try {
      const result = await pingDevice(acumaAppType);
      setModalMessage(result.message || "Request processed successfully!");
      setModalType(result.success === false ? "error" : "");
      setShowModal(true);
      await loadLatestDevice(selectedType);
    } catch (err) {
      setModalMessage(err?.message || "Unable to sync device info.");
      setModalType("error");
      setShowModal(true);
    } finally {
      setSyncing(false);
    }
  }, [selectedType, loadLatestDevice]);

  const handleGenerateCode = useCallback(async () => {
    if (!patientId) return;

    setGeneratingCode(true);
    try {
      const codePayload = await fetchWatchSignInCode(patientId, true);
      applySignInCode(codePayload);
    } catch (err) {
      console.log("Unable to generate watch sign-in code", err);
      setModalMessage(err?.message || "Unable to generate watch sign-in code.");
      setModalType("error");
      setShowModal(true);
    } finally {
      setGeneratingCode(false);
    }
  }, [patientId, applySignInCode]);

  const handleCodeExpire = useCallback(() => {
    // Keep expired code visible so New code can be enabled on the verification card.
  }, []);

  const handleCopyCode = useCallback(async () => {
    if (!signInCode) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(signInCode);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log("Unable to copy sign-in code", err);
    }
  }, [signInCode]);

  const handleModalAction = useCallback(() => {
    setShowModal(false);
    setModalMessage("");
    setModalType("");
  }, []);

  const openSetupGuide = useCallback(() => {
    const osName = String(device?.os || "").toLowerCase();
    setSetupGuidePlatform(
      /ios|iphone|ipad|watchos/.test(osName) ? "ios" : "android",
    );
    setShowSetupGuide(true);
  }, [device]);

  const closeSetupGuide = useCallback(() => setShowSetupGuide(false), []);

  const watchSignInSection = isWatch ? (
    signInCode ? (
      <WatchSignInCodeCard
        code={signInCode}
        expiresInSeconds={codeExpiresInSeconds}
        generating={generatingCode}
        onGenerate={handleGenerateCode}
        onExpire={handleCodeExpire}
      />
    ) : (
      <SignInCodePanel
        title="Watch Sign-in Code"
        description="Generate a temporary code to pair or sign in on your watch."
        code={null}
        expiresInSeconds={codeExpiresInSeconds}
        generateLabel="Generate Sign-in Code"
        regenerating={generatingCode}
        onGenerate={handleGenerateCode}
        onCopy={handleCopyCode}
        copied={copied}
      />
    )
  ) : null;

  return (
    <div>
      <SideMenuComponentsHeader title="Connected Devices" />
      <CustomLoader
        loading={
          hasActiveSubscription &&
          (deviceLoading || codeLoading || generatingCode || syncing)
        }
      />
      <CustomModal
        header=""
        name=""
        size="sm"
        title={modalMessage}
        show={showModal}
        confirmButtons={false}
        type={modalType}
        action={handleModalAction}
      />
      <Modal show={showSetupGuide} onHide={closeSetupGuide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className={styles.setupGuideModalTitle}>
            App Setup and Installation Guide
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.setupGuideModalBody}>
          <div className={styles.setupGuideToolbar}>
            <SegmentedControl
              options={SETUP_GUIDE_OPTIONS}
              value={setupGuidePlatform}
              onChange={setSetupGuidePlatform}
              ariaLabel="Guide platform"
            />
            <a
              href={SETUP_GUIDES[setupGuidePlatform].src}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.setupGuideOpenLink}
            >
              Open in new tab
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </a>
          </div>
          <iframe
            key={setupGuidePlatform}
            title={`${SETUP_GUIDES[setupGuidePlatform].label} App Setup and Installation Guide`}
            src={SETUP_GUIDES[setupGuidePlatform].src}
            className={styles.setupGuideFrame}
          />
        </Modal.Body>
      </Modal>
      <Row className={styles.mainGrid}>
        <Col xs={12} sm={6} md={3} className={styles.sideStyle}>
          <SideDetails />
        </Col>
        <Col xs={12} sm={6} md={9}>
          <div className={styles.page}>
            <div className={styles.infoHeader}>
              <div className={styles.infoHeaderText}>
                <h1 className={styles.title}>Device Information</h1>
                {hasActiveSubscription ? (
                  <p className={styles.lastSynced}>Last Synced: {lastSynced}</p>
                ) : null}
              </div>
              {hasActiveSubscription ? (
                <OutlineActionButton
                  label="Sync Device Info"
                  icon={faSpinner}
                  loading={syncing}
                  onClick={handleSyncDeviceInfo}
                />
              ) : null}
            </div>

            <div className={styles.setupGuideBanner}>
              <div className={styles.setupGuideIconWrap} aria-hidden="true">
                <FontAwesomeIcon icon={faBookOpen} />
              </div>
              <div className={styles.setupGuideText}>
                <b>Getting started:</b> Refer to the{" "}
                <button
                  type="button"
                  className={styles.setupGuideLink}
                  onClick={openSetupGuide}
                >
                  App Setup and Installation Guide
                </button>{" "}
                for step-by-step instructions.
              </div>
              {/* <button
                type="button"
                className={styles.setupGuideBtn}
                onClick={openSetupGuide}
              >
                View Guide
              </button> */}
            </div>

            {!hasActiveSubscription ? (
              <div className={styles.subscriptionMessage}>
                <label>Not included in your current subscription plan</label>
              </div>
            ) : (
              <>
                <div className={styles.switcherRow}>
                  <SegmentedControl
                    options={DEVICE_OPTIONS}
                    value={selectedType}
                    onChange={handleTypeChange}
                    ariaLabel="Device type"
                  />
                </div>

                <section
                  className={`${styles.card} ${
                    isWatch ? styles.cardWatch : styles.cardMobile
                  }`}
                >
                  <div
                    className={`${styles.cardAccentBar} ${
                      isWatch ? styles.accentWatch : styles.accentMobile
                    }`}
                  />

                  {hasDeviceData ? (
                    <>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardHeaderMain}>
                          <div
                            className={`${styles.deviceIcon} ${
                              isWatch
                                ? styles.deviceIconWatch
                                : styles.deviceIconMobile
                            }`}
                            aria-hidden="true"
                          >
                            <FontAwesomeIcon
                              icon={isWatch ? faClock : faMobileScreenButton}
                            />
                          </div>
                          <div>
                            <h2 className={styles.cardTitle}>
                              {device.modelName || selectedType}
                            </h2>
                            <p className={styles.cardMeta}>
                              {[device.manufacturer, device.os]
                                .filter(Boolean)
                                .join(" · ") || "Device details"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <InfoDetailsList items={detailItems} />
                    </>
                  ) : (
                    <div className={styles.emptyBanner} role="status">
                      {emptyDeviceMessage}
                    </div>
                  )}

                  {watchSignInSection}
                </section>
              </>
            )}

            {/* <PermissionDetails
              patientId={patientId}
              deviceType={selectedType}
              device={device}
            /> */}
          </div>
        </Col>
      </Row>
      <Footer />
    </div>
  );
};

export default ConnectedDevice;
